<template>
  <div class="author-page">
    <a-sticky-banner
      :sticky-position="STICKY_BANNER_POSITION.TOP"
      :banner-settings="BANNER_SETTINGS.AUTHOR_PAGE.STICKY_TO_TOP"
    />
    <a-breadcrumbs v-bind="breadcrumbsProps" />
    <author-details ref="authorDetails" :author="author" />
    <author-articles-sort @input="onChangeSorting" />
    <a-paginated-news-list
      ref="paginatedNewsList"
      :pagination="articles.pagination"
      :pages-count="articles.pagesCount"
      :hide-categories-strip="true"
      :hide-most-viewed-strip="true"
      :show-telegram-widget="false"
      :all-articles="articles.allArticles"
      :in-progress="inProgress"
      :banner-position="$_hydrationHelpers_mobileOrDesktopValue(2, 3)"
      @get-new-chunk="getNextArticleChunk"
    />
    <a-pagination-links-for-google
      :page="articles.pagination.page"
      :pages-count="articles.pagesCount"
      :non-paginated-url="canonicalUrlWithoutPagination"
    />
    <a-url-pagination
      :item-count="articles.allArticles.length"
      :pagination.sync="articles.pagination"
      :initial-page="articles.initialPage"
      :pages-count="articles.pagesCount"
      :item-selector="$options.consts.ARTICLE_SLOT_SELECTOR"
      :non-paginated-url="canonicalUrlWithoutPagination"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import mixins from '@/utils/mixins'
import {
  METHOD_NAME_TO_RUN_ON_LOGOUT,
  METHOD_NAME_TO_RUN_ON_LOGIN
} from '@/utils/mixins/run-method-on-login-logout'
import { ARTICLE_SLOT_CLASS_NAME } from 'shared/APaginatedNewsList/enums'
import { SORT_TYPE } from 'shared/ASortByLatestOldest'
import AuthorDetails from '@/components/_sections/author/AuthorDetails'
import { ROUTE_NAME } from 'enums/routes'
import ABreadcrumbs, { BREADCRUMB_NAMING } from 'shared/ABreadcrumbs'
import { PAGE_SCHEME_TYPE } from '@/utils/enums/pageSchemes'
import { throwPageNotFoundError } from '@/plugins/error'
import { BANNER_SETTINGS } from 'enums/banners/banner-settings'
import AStickyBanner from 'shared/AStickyBanner'
import { STICKY_BANNER_POSITION } from 'enums/banners/sticky-banner'
import { SOURCE } from 'enums/source'
import APaginationLinksForGoogle from 'shared/APaginationLinksForGoogle'
import { getPageNumberFromRoute } from '@/plugins/helper'
import { applyFallback } from '@/utils/helpers'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import AUrlPagination from 'shared/AUrlPagination'
import { METHOD_NAME, PENDING_ACTION } from 'enums/pending-actions'
import { generateAuthorUrl } from '@/utils/helpers/url-generators'
import {
  generatePaginatedCanonicalUrl,
  checkIfNonCanonicalPath
} from '@/utils/helpers/canonical-links'
import APaginatedNewsList from 'shared/APaginatedNewsList'
import { handleNextChunks } from '@/utils/helpers/handle-duplicates'
import { redirectHandler } from '@/utils/helpers/redirects/helpers'
import { pick } from 'ramda'
import AuthorArticlesSort from '@/components/_sections/author/AuthorArticlesSort/index.vue'

const DEFAULT_SORTING = SORT_TYPE.LATEST

const DEFAULT_TAKE = 25

const AUTHOR_MODEL = {
  PhotoUrl: null,
  FirstName: '',
  LastName: '',
  Job: '',
  Company: '',
  Bio: '',
  FacebookUrl: '',
  TwitterUrl: '',
  LinkedInUrl: '',
  TelegramUrl: '',
  Email: '',
  IsFollowed: false
}

async function getAuthorPageData({ ctx, route, store }) {
  const dispatch = store.dispatch
  const initialPage = getPageNumberFromRoute(route)
  const slug = route.params.slug.toLowerCase()

  const pagination = {
    take: DEFAULT_TAKE,
    page: initialPage
  }
  const authorParams = { slug, source: SOURCE.FM }

  const authorArticlesParams = {
    slug,
    publishedOn: DEFAULT_SORTING,
    ...pagination
  }

  const [author, { Articles, PagesCount }] = await Promise.all([
    dispatch('author/requestAuthor', authorParams),
    dispatch('author/requestAuthorArticles', authorArticlesParams)
  ])

  if (PagesCount && initialPage > PagesCount) {
    return throwPageNotFoundError(ctx, 'AuthorPage')
  }

  return {
    author: {
      ...applyFallback(author, AUTHOR_MODEL)
    },
    articles: {
      allArticles: Articles,
      publishedOn: DEFAULT_SORTING,
      initialPage,
      pagesCount: PagesCount,
      pagination,
      shift: 0
    }
  }
}

function generateDefaultAuthorContentDataModel({ route }) {
  const initialPage = getPageNumberFromRoute(route)

  return {
    author: {},
    articles: {
      allArticles: [],
      publishedOn: DEFAULT_SORTING,
      initialPage,
      pagesCount: 0,
      pagination: {
        take: DEFAULT_TAKE,
        page: initialPage
      },
      shift: 0
    }
  }
}

export default {
  name: 'AuthorPage',
  scrollToTop: true,
  validate({ params: { slug } }) {
    return !!slug
  },
  mixins: [
    mixins.refreshDataHandler,
    mixins.runMethodOnLoginLogout,
    mixins.setPageLoaded,
    hydrationHelpers,
    mixins.refreshDataListenersFactory({
      refreshHandlerByType: {
        [PENDING_ACTION.SAVE_ARTICLE]: METHOD_NAME.UPDATE_IS_SAVED_VALUE,
        [PENDING_ACTION.FOLLOW_TAG]: METHOD_NAME.UPDATE_IS_FOLLOWED_TAG_VALUE,
        [PENDING_ACTION.FOLLOW_AUTHOR]:
          METHOD_NAME.UPDATE_IS_FOLLOWED_AUTHOR_VALUE
      }
    }),
    mixins.delayedContentRefresh
  ],
  components: {
    AuthorArticlesSort,
    APaginatedNewsList,
    AStickyBanner,
    AuthorDetails,
    ABreadcrumbs,
    APaginationLinksForGoogle,
    AUrlPagination
  },
  head() {
    const {
      Job: jobTitle,
      FirstName,
      LastName,
      Bio,
      PhotoUrl,
      LinkedInUrl,
      TwitterUrl,
      FacebookUrl
    } = this.author
    const name = `${FirstName} ${LastName}`
    const url = this.$helper.getFullPath(this)
    const sameAs = [LinkedInUrl, TwitterUrl, FacebookUrl].filter(Boolean)

    return this.$generateMetaTags({
      titleParam: name,
      descriptionParam: name,
      schemes: [
        {
          type: PAGE_SCHEME_TYPE.PERSON,
          data: {
            name,
            jobTitle,
            url,
            description: this.$helper.htmlToText(Bio),
            image: PhotoUrl,
            sameAs,
            worksFor: {
              '@type': 'Organization',
              name: 'Finance Magnates',
              url: this.$env.DOMAIN_URL
            }
          }
        }
      ],
      canonicalUrl: this.canonicalUrl
    })
  },
  async asyncData(ctx) {
    const { path: currentPath, fullPath } = ctx.route

    try {
      const authorData = await getAuthorPageData({
        ctx,
        route: ctx.route,
        store: ctx.store
      })

      const canonicalUrlWithoutPagination = generateAuthorUrl(
        authorData.author,
        ctx
      )
      const canonicalUrl = generatePaginatedCanonicalUrl({
        canonicalUrlWithoutPagination,
        path: ctx.route.path
      })

      if (checkIfNonCanonicalPath(ctx, { currentPath, canonicalUrl })) {
        redirectHandler({ ctx, to: canonicalUrl })
        return
      }

      return {
        ...authorData,
        canonicalUrl,
        canonicalUrlWithoutPagination
      }
    } catch (err) {
      ctx.app.$errorHandler(err, ctx, {
        showErrorPage: true,
        showMessage: false
      })
    } finally {
      await ctx.store.dispatch('router/removeRouteFromQueue', fullPath)
    }
  },
  consts: {
    ARTICLE_SLOT_SELECTOR: `.${ARTICLE_SLOT_CLASS_NAME}`
  },
  data() {
    return {
      ...generateDefaultAuthorContentDataModel({ route: this.$route }),
      canonicalUrl: this.$route.path,
      canonicalUrlWithoutPagination: this.$route.path,
      BANNER_SETTINGS,
      STICKY_BANNER_POSITION,
      inProgress: false
    }
  },
  computed: {
    fullName() {
      return this.$helper.joinStringsWithSpaces([
        this.author.FirstName,
        this.author.LastName
      ])
    },
    breadcrumbsDynamicNames() {
      return {
        [ROUTE_NAME.AUTHOR_SLUG]: this.fullName
      }
    },
    breadcrumbsProps() {
      return {
        breadcrumbsDynamicNames: this.breadcrumbsDynamicNames,
        breadcrumbsNaming: BREADCRUMB_NAMING.CAPITALIZE_EACH_WORD
      }
    }
  },
  methods: {
    ...mapActions({
      requestAuthorArticles: 'author/requestAuthorArticles'
    }),
    async getArticles(
      pagination = {
        take: DEFAULT_TAKE,
        page: 0
      }
    ) {
      try {
        const { Articles, PagesCount } = await this.requestAuthorArticles({
          slug: this.$route.params.slug.toLowerCase(),
          publishedOn: this.articles.publishedOn,
          ...pagination
        })
        this.articles.pagesCount = PagesCount

        return Articles
      } catch (err) {
        this.$errorHandler(err, this, {
          showErrorPage: true,
          showMessage: false
        })
      }
    },
    getNextArticleChunk() {
      this.articles.pagination.page++
      handleNextChunks({
        shift: this.articles.shift,
        pagination: this.articles.pagination,
        pagesCount: this.articles.pagesCount,
        setInProgressTrueFn: () => {
          this.inProgress = true
        },
        setInProgressFalseFn: () => {
          this.inProgress = false
        },
        setShiftFn: newVal => {
          this.articles.shift = newVal
        },
        requestNextChunkFn: this.getArticles.bind(this),
        addNewArticlesFn: async newArticles => {
          this.articles.allArticles = [
            ...this.articles.allArticles,
            ...newArticles
          ]
          await this.$nextTick()
        },
        getExistingArticleIdsFn: () =>
          this.articles.allArticles.map(({ id }) => id)
      })
    },
    async onChangeSorting(sorting) {
      if (sorting === this.articles.publishedOn) return

      this.articles.publishedOn = sorting
      this.clearData()

      this.$refs.paginatedNewsList.rerenderNonHydratedComponents()
      this.articles.allArticles = await this.getArticles()
    },
    clearData() {
      this.articles.allArticles = []
      this.articles.initialPage = 0
      this.articles.pagesCount = 0
      this.articles.pagination.page = 0
    },
    [METHOD_NAME.UPDATE_IS_SAVED_VALUE](payload) {
      this.articles.allArticles = this.$_refreshDataHandler_updateIsSavedValue(
        payload,
        this.articles.allArticles
      )
    },
    [METHOD_NAME.UPDATE_IS_FOLLOWED_TAG_VALUE](payload) {
      this.articles.allArticles = this.$_refreshDataHandler_updateIsFollowedTagValue(
        {
          ...payload,
          articles: this.articles.allArticles
        }
      )
    },
    [METHOD_NAME.UPDATE_IS_FOLLOWED_AUTHOR_VALUE](payload) {
      if (this.author.Id === payload.id) {
        this.author.IsFollowed = payload.value
      }
    },
    [METHOD_NAME_TO_RUN_ON_LOGOUT]() {
      this.articles.allArticles = this.$_refreshDataHandler_resetActionIconValues(
        this.articles.allArticles
      )
      this.author.IsFollowed = false
    },
    async [METHOD_NAME_TO_RUN_ON_LOGIN]() {
      const {
        Article,
        Tag
      } = await this.$_refreshDataHandler_getSavedIdsOfEntities([
        this.$_refreshDataHandler_savedEntity.ARTICLE,
        this.$_refreshDataHandler_savedEntity.TAG
      ])
      this.articles.allArticles = this.$_refreshDataHandler_updateArticleSavedEntities(
        {
          articleIds: Article,
          tagIds: Tag
        },
        this.articles.allArticles
      )
      this.author.IsFollowed = await this.getIsFollowedAuthor()
    },
    getIsFollowedAuthor() {
      const slug = this.$route.params.slug.toLowerCase()
      return this.$_refreshDataHandler_getIsFollowedAuthorValue({ slug })
    }
  },
  mounted() {
    const dataKeysForDelayedContentRefresh = Object.keys(
      generateDefaultAuthorContentDataModel({ route: this.$route })
    )

    this.$_delayedContentRefresh_run({
      getOldDataFn: () => pick(dataKeysForDelayedContentRefresh, this),
      requestNewDataFn: async () => {
        const newData = await getAuthorPageData({
          ctx: this,
          route: this.$route,
          store: this.$store
        })

        return pick(dataKeysForDelayedContentRefresh, newData)
      },
      dataComparisonKeysToOmitDeep: ['lastModified'],
      dataChangedCallbackFn: newData => {
        Object.assign(this, pick(dataKeysForDelayedContentRefresh, newData))
        this.$refs.paginatedNewsList.rerenderNonHydratedComponents()
        this.$refs.authorDetails.rerenderNonHydratedComponents()
      }
    })
  }
}
</script>

<style lang="scss" scoped>
.author-page {
  @include mobile {
    margin-top: 50px;
  }
}
</style>
